import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import PropTypes from "prop-types";
import * as yup from "yup";
import { formValidate } from "helpers/form_helper";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import { useChangePassword } from "hooks";

const ChangePassword = props => {
  const { isOpenModal, toggleModal } = props;
  const { t } = useTranslation("translation");

  const { mutate: onChangeUpdatePassword } = useChangePassword()

  useEffect(() => {
    isOpenModal && reset(initDefaultField);
  }, [isOpenModal]);

  const initDefaultField = {
    old_password: "",
    new_password: "",
    confirm_password: "",
  };

  const schema = yup.object().shape({
    old_password: yup
      .string()
      .required(t("form.validate.required", { title: t("form.old_password") })),
    new_password: yup
      .string()
      .required(t("form.validate.required", { title: t("form.new_password") }))
      .min(6, t("form.validate.char_min", { min: 6 }))
      .max(250, t("form.validate.char_max", { max: 250 })),
    confirm_password: yup
      .string()
      .required(t("form.validate.required", { title: t("form.confirm_password") }))
      .oneOf([yup.ref("new_password")], t("form.validate.not_match")),
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, touchedFields, isSubmitting },
    control,
    setValue,
    getValues,
    setError,
    register,
    unregister,
    trigger,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: initDefaultField,
  });

  const onChangePassword = values => {
    onChangeUpdatePassword({ data: values })
    toggleModal()
  };

  return (
    <React.Fragment>
      <Modal
        size="xl"
        isOpen={isOpenModal}
        role="dialog"
        autoFocus={true}
        centered={true}
        className="exampleModal"
        toggle={toggleModal}
      >
        <div className="modal-content">
          <ModalHeader toggle={toggleModal}>{t("change_password")}</ModalHeader>
          <ModalBody>
            <Row className="row-cols-1">
              <Col>
                <FormGroup className="mb-3">
                  <Label htmlFor="old_password">
                    {t("form.old_password")}{" "}
                    <span className="text-danger">*</span>
                  </Label>
                  <input
                    type="text"
                    className={`form-control ${formValidate(
                      errors,
                      touchedFields,
                      "old_password"
                    )}`}
                    placeholder={t("placeholder.enter", {
                      title: t("form.old_password"),
                    })}
                    {...register("old_password")}
                  />
                  {errors.old_password && (
                    <MessageFormValidate error={errors.old_password.message} />
                  )}
                </FormGroup>
              </Col>

              <Col>
                <FormGroup className="mb-3">
                  <Label htmlFor="new_password">
                    {t("form.new_password")}{" "}
                    <span className="text-danger">*</span>
                  </Label>
                  <input
                    type="text"
                    className={`form-control ${formValidate(
                      errors,
                      touchedFields,
                      "new_password"
                    )}`}
                    placeholder={t("placeholder.enter", {
                      title: t("form.new_password"),
                    })}
                    {...register("new_password")}
                  />
                  {errors.new_password && (
                    <MessageFormValidate error={errors.new_password.message} />
                  )}
                </FormGroup>
              </Col>

              <Col>
                <FormGroup className="mb-3">
                  <Label htmlFor="confirm_password">
                    {t("form.confirm_password")}{" "}
                    <span className="text-danger">*</span>
                  </Label>
                  <input
                    type="text"
                    className={`form-control ${formValidate(
                      errors,
                      touchedFields,
                      "confirm_password"
                    )}`}
                    placeholder={t("placeholder.enter", {
                      title: t("form.confirm_password"),
                    })}
                    {...register("confirm_password")}
                  />
                  {errors.confirm_password && (
                    <MessageFormValidate
                      error={errors.confirm_password.message}
                    />
                  )}
                </FormGroup>
              </Col>
            </Row>
            <Row className="justify-content-end">
              <Col className="col-auto">
                <Button
                  color="primary"
                  className="btn-label"
                  type="button"
                  onClick={() => {
                    handleSubmit(onChangePassword)();
                  }}
                >
                  <i className="far fa-save fa-lg label-icon"></i>
                  {t("form.save")}
                </Button>
              </Col>
            </Row>
          </ModalBody>
        </div>
      </Modal>
    </React.Fragment>
  );
};

ChangePassword.propTypes = {
  isOpenModal: PropTypes.bool.isRequired,
  toggleModal: PropTypes.func.isRequired,
};

ChangePassword.defaultProps = {
  isOpenModal: false,
};

export default ChangePassword;
